// SystemStatus.jsx - Debug component for HPC report system
import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

const SystemStatus = ({ onClose }) => {
    const [status, setStatus] = useState({
        api: 'checking',
        auth: 'checking',
        tasks: 'checking'
    });
    const [details, setDetails] = useState({});

    useEffect(() => {
        checkSystemStatus();
    }, []);

    const checkSystemStatus = async () => {
        const accessToken = localStorage.getItem("accessToken");
        
        // Check API connectivity
        try {
            const response = await fetch(`${API_BASE_URL}/user/stage`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                setStatus(prev => ({ ...prev, api: 'ok', auth: 'ok' }));
                const data = await response.json();
                setDetails(prev => ({ ...prev, userStage: data.stage }));
            } else {
                setStatus(prev => ({ ...prev, api: 'error', auth: response.status === 401 ? 'error' : 'ok' }));
            }
        } catch (error) {
            setStatus(prev => ({ ...prev, api: 'error' }));
            setDetails(prev => ({ ...prev, apiError: error.message }));
        }

        // Check task system health
        try {
            const response = await fetch(`${API_BASE_URL}/tasks/health`);
            if (response.ok) {
                const data = await response.json();
                setStatus(prev => ({ ...prev, tasks: 'ok' }));
                setDetails(prev => ({ ...prev, taskHealth: data }));
            } else {
                setStatus(prev => ({ ...prev, tasks: 'error' }));
            }
        } catch (error) {
            setStatus(prev => ({ ...prev, tasks: 'error' }));
            setDetails(prev => ({ ...prev, taskError: error.message }));
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'ok': return '✅';
            case 'error': return '❌';
            case 'checking': return '⏳';
            default: return '❓';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'ok': return 'text-green-600';
            case 'error': return 'text-red-600';
            case 'checking': return 'text-yellow-600';
            default: return 'text-gray-600';
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">System Status</h3>
                    <button 
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        ✕
                    </button>
                </div>

                <div className="space-y-3">
                    <div className="flex items-center justify-between">
                        <span>API Connection</span>
                        <span className={`flex items-center ${getStatusColor(status.api)}`}>
                            {getStatusIcon(status.api)} {status.api}
                        </span>
                    </div>

                    <div className="flex items-center justify-between">
                        <span>Authentication</span>
                        <span className={`flex items-center ${getStatusColor(status.auth)}`}>
                            {getStatusIcon(status.auth)} {status.auth}
                        </span>
                    </div>

                    <div className="flex items-center justify-between">
                        <span>Task System</span>
                        <span className={`flex items-center ${getStatusColor(status.tasks)}`}>
                            {getStatusIcon(status.tasks)} {status.tasks}
                        </span>
                    </div>
                </div>

                {details.userStage && (
                    <div className="mt-4 p-3 bg-gray-100 rounded">
                        <p className="text-sm"><strong>User Stage:</strong> {details.userStage}</p>
                    </div>
                )}

                {details.taskHealth && (
                    <div className="mt-4 p-3 bg-gray-100 rounded">
                        <p className="text-sm"><strong>Task System:</strong></p>
                        <ul className="text-xs mt-1 space-y-1">
                            <li>Total Tasks: {details.taskHealth.total_tasks}</li>
                            <li>Running: {details.taskHealth.running_tasks}</li>
                            <li>Pending: {details.taskHealth.pending_tasks}</li>
                            <li>Completed: {details.taskHealth.completed_tasks}</li>
                            <li>Failed: {details.taskHealth.failed_tasks}</li>
                        </ul>
                    </div>
                )}

                {(details.apiError || details.taskError) && (
                    <div className="mt-4 p-3 bg-red-100 rounded">
                        <p className="text-sm text-red-700"><strong>Errors:</strong></p>
                        {details.apiError && <p className="text-xs text-red-600">API: {details.apiError}</p>}
                        {details.taskError && <p className="text-xs text-red-600">Tasks: {details.taskError}</p>}
                    </div>
                )}

                <div className="mt-4 flex space-x-2">
                    <button 
                        onClick={checkSystemStatus}
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded text-sm hover:bg-blue-700"
                    >
                        Refresh
                    </button>
                    <button 
                        onClick={onClose}
                        className="flex-1 bg-gray-600 text-white py-2 px-4 rounded text-sm hover:bg-gray-700"
                    >
                        Close
                    </button>
                </div>

                <div className="mt-3 text-xs text-gray-500 text-center">
                    API: {API_BASE_URL}
                </div>
            </div>
        </div>
    );
};

export default SystemStatus;